import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { LoginComponent } from "./pages/login/login.component";
import { PageNotFoundComponent } from "./pages/page-not-found/page-not-found.component";
import { ProjectsComponent } from "./pages/projects/projects.component";
import { HasRightsGuard } from "./guards/has-rights.guard";
import { UserRight } from "./WebApiClient";
import { UsersComponent } from "./pages/users/users.component";
import { TimelineComponent as ProjectTimelineComponent } from "./pages/project-details/timeline/timeline.component";
import { ComponentsComponent as ProjectComponentsComponent } from "./pages/project-details/components/components.component";
import { ReportsComponent as ProjectReportsComponent } from "./pages/project-details/reports/reports.component";
import { ProjectOverviewComponent } from "./pages/project-details/project-overview/project-overview.component";
import { GangsComponent } from "./pages/project-details/gangs/gangs.component";
import { FilesComponent } from "./pages/project-details/files/files.component";
import { LinkComponent } from "./pages/link/link.component";
import { LinksComponent } from "./pages/project-details/links/links.component";
import { UnsubscribeComponent } from "./pages/unsubscribe/unsubscribe.component";
import { MailRecipientComponent } from "./pages/project-details/mail-recipient/mail-recipient.component";
import { CreateProjectComponent } from "./pages/create-project/create-project.component";
import { EditProjectComponent } from "./pages/edit-project/edit-project.component";
import { ProjectDocumentsComponent } from "./pages/project-details/project-documents/project-document.component";
import { DonwloadProjectDocumentsComponent } from "./pages/download-project-documents/download-project-documents";
import { CompletedProjectsComponent } from "./pages/completed-projects/completed-projects.component";
import { CompletedProjectQuestionnaireComponent } from "./pages/completed-project-questionnaire/completed-project-questionnaire.component";
import { ProjectReportAndStastiticsComponent } from "./pages/project-details/project-report/project-report.component";
import { MailLogComponent } from "./pages/project-details/mail-log/mail-log.component";
import { DailyComponent } from "./pages/unsub/daily/daily.component";
import { FrontPageComponent } from "./pages/front-page/front-page.component";
import { StorageComponent } from "./pages/storage/storage.component";
import { StorageItemsComponent } from "./pages/storage-items/storage-items.component";
import { StorageItemDetailsComponent } from "./pages/storage-item-details/storage-item-details.component";
import { SustainabilityComponent } from './pages/sustainability/sustainability.component';
import { SustainabilityProjectCo2ConsumptionComponent } from "./pages/sustainability-projectco2-consumption/sustainability-projectco2-consumption.component";
import { SustainabilityESGComponent } from "./pages/sustainability-esg/sustainability-esg.component";
import { NewPasswordComponent } from "./pages/new-password/new-password.component";
import { ForgottenPasswordComponent } from "./pages/forgotten-password/forgotten-password.component";
import { SustainabilityDisclaimerComponent } from "./pages/sustainability-disclaimer/sustainability-disclaimer.component";
import { TerminalStatementComponent } from "./pages/project-details/terminalStatement/terminalStatement.component";
import { EditTerminalStatementComponent } from "./pages/project-details/edit-terminalStatement/edit-terminalStatement.component";
import { OccurenceTypesComponent } from "./pages/occurence-types/occurence-types.component";
import { CanDeactivateBlockNavigationIfChange } from "./guards/block-onchanged.guard";
import { TerminalComponent } from "./pages/terminal/terminal.component";
import { DashboardComponent } from "./pages/terminal/dashboard/dashboard.component";

const routes: Routes = [
    { path: "login", component: LoginComponent },
    { path: "link/:id", component: LinkComponent },
    { path: "forgottenpassword", component: ForgottenPasswordComponent },
    {
        path: "newpassword",
        component: NewPasswordComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewFrontPage] },
    },
    {
        path: "projects/new",
        component: CreateProjectComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.EditProject] },
    },
    {
        path: "projects",
        component: ProjectsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/completed",
        component: CompletedProjectsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/occurencetypes",
        component: OccurenceTypesComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOccurenceTypes] },
    },
    {
        path: "users",
        component: UsersComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.ListUsers] },
    },
    { path: "unsubscribe/:id", component: UnsubscribeComponent },
    { path: "unsub/daily/:id", component: DailyComponent },
    {
        path: "projects/:id/edit",
        component: EditProjectComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.EditProject] },
    },
    {
        path: "projects/:id",
        pathMatch: "full",
        redirectTo: "projects/:id/overview",
    },
    {
        path: "projects/:id/overview",
        component: ProjectOverviewComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/timeline",
        component: ProjectTimelineComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/terminalStatement",
        component: TerminalStatementComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/terminalStatementEdit",
        component: EditTerminalStatementComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.EditProject] },
    },
    {
        path: "projects/:id/components",
        component: ProjectComponentsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/gangs",
        component: GangsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/reports",
        component: ProjectReportsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.GetProjectReportsForOwnProjects] },
    },
    {
        path: "projects/:id/files",
        component: FilesComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.DownloadProjectFilesOnOwnProject] },
    },
    {
        path: "projects/:id/mail",
        component: MailRecipientComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.GetMailingList] },
    },
    {
        path: "projects/:id/links",
        component: LinksComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.CreateLinkForCustomerProject] },
    },
    {
        path: "projects/:id/projectDocuments",
        component: ProjectDocumentsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.DownloadProjectFilesOnOwnProject] },
    },
    {
        path: "projects/:id/downloadProjectDocuments",
        component: DonwloadProjectDocumentsComponent,
    },
    {
        path: "questionnaire/:qid",
        component: CompletedProjectQuestionnaireComponent,
    },
    {
        path: "projects/:id/projectreport",
        component: ProjectReportAndStastiticsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },
    {
        path: "projects/:id/maillog",
        component: MailLogComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ListOwnProjects] },
    },

    {
        path: "storage",
        redirectTo: "storage/items", // TODO: remove this redirect
        pathMatch: "full",
        //component: StorageComponent,
        //canActivate: [HasRightsGuard],
        //data: { rights: [UserRight.ViewStorage] },
    },
    {
        path: "storage/items",
        component: StorageItemsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewStorage] },
    },
    {
        path: "storage/items/:id",
        component: StorageItemDetailsComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewStorage] },
    },
    {
        path: "sustainability",
        component: SustainabilityComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewSustainability] },
    },
    {
        path: "sustainability/co2consumption",
        component: SustainabilityProjectCo2ConsumptionComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.ViewSustainability] },
    },
    {
        path: "sustainability/esg",
        component: SustainabilityESGComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.ViewSustainability] },
    },
    {
        path: "sustainability/disclaimer",
        component: SustainabilityDisclaimerComponent,
        canActivate: [HasRightsGuard],
        canDeactivate: [CanDeactivateBlockNavigationIfChange],
        data: { rights: [UserRight.EditSustainability] },
    },
    {
        path: "terminal",
        component: TerminalComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewTerminalPage] },
    },
    {
        path: "terminal/dashboard",
        component: DashboardComponent,
        canActivate: [HasRightsGuard],
        data: { rights: [UserRight.ViewTerminalPage] },
    },
    {
        path: "",
        pathMatch: "full" ,
        component: FrontPageComponent,
        canActivate: [HasRightsGuard],
        data: {rights: [UserRight.ViewFrontPage]},
    },
    { path: "**", component: PageNotFoundComponent },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes ),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
